import type { ShipMode } from "./modes";

export type AddressField = { label: string; value: string; note?: string };

export type ResolvedAddressField = AddressField & { carriesCode: boolean };

export type RouteAddress = {
  fields: AddressField[];
  packing?: string;
  packingIsMandatory?: boolean;
};

const PP = "{code}";

export const ADDRESSES: Record<string, Partial<Record<ShipMode, RouteAddress>>> = {
  US: {
    air: {
      fields: [
        { label: "First name", value: "KeBay IML", note: "The depot reference. Leave it exactly as shown." },
        { label: "Last name", value: `Parcelpoint ${PP}`, note: "Your ParcelPoint code. This is what ties the box to you." },
        { label: "Address line 1", value: "330 Tompkins Ave" },
        { label: "Address line 2", value: "Reception Desk #3623", note: "Never drop this line." },
        { label: "City", value: "Staten Island" },
        { label: "State", value: "NY" },
        { label: "ZIP code", value: "10304" },
        { label: "Country", value: "United States" },
        { label: "Phone", value: "+1 516-218-1731", note: "The warehouse number, not yours." },
      ],
      packing: "Cut-off is Monday afternoon.",
    },
    sea: {
      fields: [
        { label: "First name", value: "KeBay HWC-SEA", note: "The depot reference. Leave it exactly as shown." },
        { label: "Last name", value: `Parcelpoint ${PP}`, note: "Your ParcelPoint code. This is what ties the box to you." },
        { label: "Address line 1", value: "17401 Nichols Ln" },
        { label: "Address line 2", value: "Unit J" },
        { label: "City", value: "Huntington Beach" },
        { label: "State", value: "CA" },
        { label: "ZIP code", value: "92647" },
        { label: "Country", value: "United States" },
        { label: "Phone", value: "+1 516-218-1731", note: "The warehouse number, not yours." },
      ],
    },
  },
  UK: {
    air: {
      fields: [
        { label: "First name", value: "KeBay Air-HWC", note: "The depot reference. Leave it exactly as shown." },
        { label: "Last name", value: `PPP ${PP}`, note: "Your ParcelPoint code. This is what ties the box to you." },
        { label: "Address line 1", value: "8 Lyon Way" },
        { label: "Address line 2", value: "Workspace 008 KeBay AIR", note: "Never drop this line." },
        { label: "Town", value: "Greenford" },
        { label: "City", value: "London" },
        { label: "Post code", value: "UB6 0BN" },
        { label: "Country", value: "United Kingdom" },
        { label: "Phone", value: "+44 20 3667 2584", note: "The warehouse number, not yours." },
      ],
      packing: "Cut-off is Monday and Thursday afternoon. Boxes ship Tuesday and Friday.",
    },
    sea: {
      fields: [
        { label: "First name", value: "KeBay Sea-HWC", note: "The depot reference. Leave it exactly as shown." },
        { label: "Last name", value: `PPP ${PP}`, note: "Your ParcelPoint code. This is what ties the box to you." },
        { label: "Address line 1", value: "8 Lyon Way" },
        { label: "Address line 2", value: "Workspace 008 KeBay SEA", note: "Never drop this line." },
        { label: "Town", value: "Greenford" },
        { label: "City", value: "London" },
        { label: "Post code", value: "UB6 0BN" },
        { label: "Country", value: "United Kingdom" },
        { label: "Phone", value: "+44 20 3667 2584", note: "The warehouse number, not yours." },
      ],
    },
  },
  DE: {
    air: {
      fields: [
        { label: "First and last name", value: `PPP ${PP}`, note: "Your ParcelPoint code. This is what ties the box to you." },
        { label: "Company", value: "GCE-KeBay", note: "The depot reference. Leave it exactly as shown." },
        { label: "Address", value: "Brunnenweg 1" },
        { label: "Town", value: "Biebesheim am Rhein" },
        { label: "State", value: "Hesse" },
        { label: "Post code", value: "64584" },
        { label: "Country", value: "Germany" },
        { label: "Phone", value: "+49 176 82302287", note: "The warehouse number, not yours." },
      ],
      packing: "Cut-off is Wednesday afternoon. Boxes ship Thursday.",
    },
    sea: {
      fields: [
        { label: "First and last name", value: `PPP ${PP}`, note: "Your ParcelPoint code. This is what ties the box to you." },
        { label: "Company", value: "GCE-KeBay-SEA", note: "The depot reference. Leave it exactly as shown." },
        { label: "Address", value: "Brunnenweg 1" },
        { label: "Town", value: "Biebesheim am Rhein" },
        { label: "State", value: "Hesse" },
        { label: "Post code", value: "64584" },
        { label: "Country", value: "Germany" },
        { label: "Phone", value: "+49 176 82302287", note: "The warehouse number, not yours." },
      ],
    },
  },
  CN: {
    air: {
      fields: [
        { label: "Recipient name", value: `KeBay Air (AIR70) PPP ${PP}`, note: "Depot reference, entry number and your ParcelPoint code, all on one line." },
        { label: "Address line 1", value: "Tongde Warehouse A4" },
        { label: "Address line 2", value: "Tongya East Street, Xicha Road" },
        { label: "Town", value: "Baiyun District" },
        { label: "City", value: "Guangzhou" },
        { label: "Country", value: "China" },
        { label: "Phone", value: "FARAH +1 951 389 0461", note: "The warehouse number, not yours." },
      ],
      packing:
        "Every box must also show your name, your phone number and the entry number AIR70 written on the outside, and a packing list must reach the warehouse, or the parcel is rejected. Air shipments must use green woven bags.",
      packingIsMandatory: true,
    },
    sea: {
      fields: [
        { label: "Recipient name", value: `KeBay Sea (SEA80) PPP ${PP}`, note: "Depot reference, entry number and your ParcelPoint code, all on one line." },
        { label: "Address line 1", value: "Tongde Warehouse A4" },
        { label: "Address line 2", value: "Tongya East Street, Xicha Road" },
        { label: "Town", value: "Baiyun District" },
        { label: "City", value: "Guangzhou" },
        { label: "Country", value: "China" },
        { label: "Phone", value: "FARAH +1 951 389 0461", note: "The warehouse number, not yours." },
      ],
      packing:
        "Every box must also show your name, your phone number and the entry number SEA80 written on the outside, and a packing list must reach the warehouse, or the parcel is rejected.",
      packingIsMandatory: true,
    },
  },
  ZA: {
    road: {
      fields: [
        { label: "First name", value: `PPP ${PP}`, note: "Your ParcelPoint code. This is what ties the box to you." },
        { label: "Last name", value: "KeBay/RAD LOGISTICS", note: "The depot reference. Leave it exactly as shown." },
        { label: "Address line 1", value: "Unit 1, 5 Star Business Park Phase 2" },
        { label: "Address line 2", value: "19 Galaxy Drive" },
        { label: "Town", value: "Honeydew" },
        { label: "Province", value: "Gauteng" },
        { label: "Post code", value: "2170" },
        { label: "Country", value: "South Africa" },
        { label: "Phone", value: "+27 711 049 465", note: "The warehouse number, not yours." },
      ],
    },
  },
};

export function resolveFields(fields: AddressField[], ppCode: string): ResolvedAddressField[] {
  return fields.map((f) => ({ ...f, value: f.value.replace(PP, ppCode), carriesCode: f.value.includes(PP) }));
}

export function toPlainAddress(fields: AddressField[]): string {
  return fields.map((f) => f.value).join("\n");
}

export function addressFor(origin: string, mode: ShipMode): RouteAddress | null {
  return ADDRESSES[origin]?.[mode] ?? null;
}
